import { useCallback, useEffect, useRef } from 'react';

/**
 * Notification sound using a real audio file + HTMLAudioElement.
 *
 * The audio element is "unlocked" on the first user interaction
 * (click/touch/key) so subsequent programmatic .play() calls work
 * even without a direct user gesture.
 */

let audioElement: HTMLAudioElement | null = null;
let audioUnlocked = false;

function getAudio(): HTMLAudioElement {
  if (!audioElement) {
    audioElement = new Audio('/notification.wav');
    audioElement.preload = 'auto';
    audioElement.volume = 0.6;
  }
  return audioElement;
}

function unlockAudio() {
  if (audioUnlocked) return;
  const audio = getAudio();
  // Play silently to unlock the element for future programmatic plays
  const origVolume = audio.volume;
  audio.volume = 0;
  audio.play()
    .then(() => {
      audio.pause();
      audio.currentTime = 0;
      audio.volume = origVolume;
      audioUnlocked = true;
    })
    .catch(() => {
      audio.volume = origVolume;
    });
}

// Register unlock on first user gesture (once)
if (typeof document !== 'undefined') {
  const gestureEvents = ['click', 'touchstart', 'keydown'];
  const onGesture = () => {
    unlockAudio();
    gestureEvents.forEach((evt) =>
      document.removeEventListener(evt, onGesture, true),
    );
  };
  gestureEvents.forEach((evt) =>
    document.addEventListener(evt, onGesture, { capture: true }),
  );
}

export function useNotificationSound() {
  const lastPlayedRef = useRef(0);

  // Also try to unlock on mount
  useEffect(() => {
    unlockAudio();
  }, []);

  const play = useCallback(() => {
    // Throttle: max once every 2 seconds
    const now = Date.now();
    if (now - lastPlayedRef.current < 2000) return;
    lastPlayedRef.current = now;

    const audio = getAudio();
    audio.currentTime = 0;
    audio.play().catch(() => {
      // Browser still blocked — nothing we can do
    });
  }, []);

  return play;
}
